
import { Component, Vue } from "vue-property-decorator";
import QrCodeScannerWrapper from "@/components/qrcode/QrCodeScannerWrapper.vue";
import KeyStatus from "@/components/keys/KeyStatus.vue";
import { regex } from "@/utils/formRules";
import { Getter } from "vuex-class";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";

type ActionType = "drop" | "withdraw";

@Component({
  components: { MylobyAutocomplete, QrCodeScannerWrapper, KeyStatus },
})
export default class Transfer extends Vue {

  @Getter private currentUser: any;
  @Getter private envCompany: any;

  private loadingQrCodeStreamer = false;
  private keys: any[] = [];
  private selectedGuest: any = null;
  private addInvitation: boolean = false;
  private DROP_ACTION = "drop";
  private WITHDRAW_ACTION = "withdraw";

  private selectedAction: ActionType | null = null;

  private headers = [
    { value: "name", text: this.$t("key.name") },
    { value: "status", text: this.$t("key.status") },
    { value: "location", text: this.$t("key.location") },
    { value: "canDo", text: this.$t("Transfer.canDoAction") },
    { value: "actions", text: null, sortable: false },
  ];

  get actionItems() {
    return [
      { text: this.$t("Transfer.actions.drop"), value: this.DROP_ACTION },
      { text: this.$t("Transfer.actions.withdraw"), value: this.WITHDRAW_ACTION },
    ];
  }

  get isCleanPossible(): boolean {
    return this.keys.some((key) => key.actionStatus !== "WAIT_FOR_ACTION");
  }

  get canPerformAction(): boolean {
    return this.selectedAction && this.keys.length !== 0;
  }

  private async mounted(): Promise<void> {
    // await this.addKeyByToken("85c1cff3-2f2c-11eb-baa8-4f4ff3258b24");
  }

  private async onDecode(decodedString: string) {
    if (!regex.token.test(decodedString)) { return; }
    this.loadingQrCodeStreamer = true;
    await this.addKeyByToken(decodedString);
    this.loadingQrCodeStreamer = false;
    await (this.$refs.qrScanner as QrCodeScannerWrapper).reloadScanner();
  }

  private async addKeyByToken(decodedString: any) {
    const contextData = await this.$store.dispatch("context/getByToken", { token: decodedString })
      .catch((e) => this.$store.commit("alerts/displayHttpError", { error: e }));
    if (!contextData?.keyId) {
      this.$store.commit("alerts/displayError", {
        msg: `${this.$i18n?.t("Transfer.error.qrCodeNotValid")} `,
      });
      return;
    }

    if (this.keys.some((k: any) => k.id === contextData.keyId)) {
      return;
    }

    const key = await this.$store.dispatch("keys/getById", {
      id: contextData.keyId, options: {
        relays: true,
        holder: true,
      },
    })
      .catch((e) => this.$store.commit("alerts/displayHttpError", { error: e }));
    if (!key) { return; }

    const canDrop = this.canDropFn(key);
    const canWithdraw = this.canWithdrawFn(key);

    this.keys.push({
      location: contextData.name,
      canDrop,
      canWithdraw,
      token: decodedString,
      actionStatus: "WAIT_FOR_ACTION",
      errorMessage: "",
      ...key,
    });
  }

  private canDropFn(key: any): boolean {
    return !(key.status === "IN_RELAY") && (!!(key.legitimateKeyAccess.keyManagementId));
  }

  private canWithdrawFn(key: any): boolean {
    return (key.status === "IN_RELAY") && (!!(key.legitimateKeyAccess.keyManagementId));
  }

  private removeKey(item: any) {
    this.keys = this.keys.filter((k) => k.id !== item.id);
  }

  private clearSuccessList() {
    this.keys = this.keys
      // on enlève tous les statuts qui finissent par "SUCCESS"
      .filter((k) => !k.actionStatus.endsWith("SUCCESS"))
      // on remet en WAIT_FOR_ACTION ceux qui finissent par "ERROR"
      .map((k) => {
        if (k.actionStatus.endsWith("ERROR")) {
          return { ...k, actionStatus: "WAIT_FOR_ACTION" };
        }
        return k;
      });
  }

  private clearList() {
    this.keys = [];
  }

  private async performAction() {

    if (this.selectedAction === this.DROP_ACTION) {
      const keysToDrop = this.keys.filter((k: any) => k.canDrop);
      await this.performActionDrop(keysToDrop);

      const keysIdByUsersId = this.keys
        .filter((k: any) => k.actionStatus === "DROP_SUCCESS")
        .reduce((acc, k) => {
          const userId = k.KeyHolding?.[0]?.User?.id;
          if (userId) {
            if (acc[userId]) {
              acc[userId].push(k.id);
            } else {
              acc[userId] = [k.id];
            }
          }
          return acc;
        }, {});

      for (const property in keysIdByUsersId) {
        if (Object.hasOwn(keysIdByUsersId, property) && keysIdByUsersId[property].length > 0) {
          await this.$store.dispatch("notifications/create", {
            keysId: keysIdByUsersId[property],
            userId: property,
            notificationType: "multi-drop",
          });
        }
      }

    } else if (this.selectedAction === this.WITHDRAW_ACTION) {
      const keysToWithdraw = this.keys.filter((k: any) => k.canWithdraw);
      await this.performActionWithdraw(keysToWithdraw);

      const keysId = this.keys.filter((k: any) => k.actionStatus === "WITHDRAW_SUCCESS").map((k: any) => k.id);
      if (keysId.length > 0) {
        await this.$store.dispatch("notifications/create", {
          keysId,
          userId: this.selectedGuest.id,
          notificationType: "multi-withdraw",
        });
      }
    }

  }

  private async performActionDrop(keys) {
    for (const key of keys) {
      key.actionStatus = "LOADING";
      await this.$store.dispatch("locations/qrCodeVerification", {
        token: key.token,
        keyId: key.id,
        keyStatus: key.status,
        dropFromRelay: true,
        moveAllSubObjects: false,
        referentId: this.currentUser.id,
        notificationType: "none",
      })
        .then(
          async (res) => {
            if (res.dropped) {
              key.actionStatus = "DROP_SUCCESS";
            } else if (!res.errorMessage && res.relayId && res.validLocation) {
              await this.$store.dispatch("keys/drop", {
                keyId: key.id,
                token: key.token,
                referentId: this.currentUser.id,
                relayId: res.relayId,
                holderId: key.KeyHolding[0]?.userId,
                keyToGuest: key.KeyHolding[0]?.userId,
                notificationType: "none",
              })
                .then((res: any) => {
                  key.actionStatus = "DROP_SUCCESS";
                })
                .catch(() => {
                  key.actionStatus = "DROP_ERROR";
                });
            } else {
              key.actionStatus = "DROP_ERROR";
            }
          },
        )
        .catch((error) => {
          this.$store.commit("alerts/displayHttpError", { error });
          key.actionStatus = "DROP_ERROR";
        });
    }
  }

  private async performActionWithdraw(keys) {
    if (!this.selectedGuest) {
      return;
    }
    for (const key of keys) {
      await this.withdrawKey(key).then(
        async (res) => {
          if (res.errorMessage === "noAccess" && this.addInvitation) {
            await this.createPunctualInvitation(key);
            await this.withdrawKey(key);
          }
        },
      );
    }
  }

  private async withdrawKey(key) {
    return this.$store.dispatch("locations/qrCodeVerification", {
      token: key.token,
      keyId: key.id,
      keyStatus: key.status,
      dropFromRelay: false,
      giveToUserId: this.selectedGuest.id,
      moveAllSubObjects: false,
      referentId: this.currentUser.id,
      notificationType: "none",
    }).then((res) => {
      key.actionStatus = res.withdrawn ? "WITHDRAW_SUCCESS" : "WITHDRAW_ERROR";
      key.errorMessage = res.errorMessage;
      return res;
    });
  }

  private async createPunctualInvitation(key) {
    await this.$store.dispatch(`keys/addGuests`, {
      id: key.id,
      users : [this.selectedGuest],
      invitation: {
        countLimited: true,
        timeLimited: false,
        notificationType: "none",
        acceptOverPrice : false,
      },
    });
  }

}
